import React, { useState } from 'react';
import {
  Title,
  Card,
  Stack,
  Group,
  Button,
  Text,
  ActionIcon,
  Loader,
  SimpleGrid,
} from '@mantine/core';
import { PageContainer } from '../../components/ui/PageContainer';
import {
  IconEdit,
  IconTrash,
  IconPlus,
  IconExternalLink,
  IconCopy,
  IconSparkles,
} from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { showToast } from '../../utils/toast';
import { getErrorMessage } from '../../utils/api';
import { CompetencyMapModal } from '../../components/competency/CompetencyMapModal';
import { CompetencyMapCopyModal } from '../../components/competency/CompetencyMapCopyModal';
import { FirstMapWizard } from '../../components/competency/FirstMapWizard';
import { CompetencyMap as CompetencyMapView } from '../../components/competency/CompetencyMap';
import { CompetencyMap, CompetencyMapData } from '../../types';
import { useDevice } from '../../hooks/useDevice';

/** Пример для пустого раздела: данные карты из локалей. */
function exampleMap(t: TFunction): CompetencyMapData {
  const key = 'competencyMaps.first.example';
  const titles = t(`${key}.grades`, { returnObjects: true }) as string[];
  const rows = t(`${key}.rows`, { returnObjects: true }) as { lane: string; cells: string[] }[];
  return {
    swimlanes: rows.map((row, r) => ({ _id: `s${r}`, title: row.lane })),
    grades: titles.map((title, g) => ({
      _id: `g${g}`,
      title,
      description: '',
      qualification: '',
      position: '',
      competencies: rows.map((row, r) => ({
        _id: `c${r}-${g}`,
        title: row.cells[g],
        swimlaneId: `s${r}`,
        sortOrder: 0,
      })),
    })),
  };
}

export const CompetencyMaps: React.FC = () => {
  const { t } = useTranslation();
  const deviceType = useDevice();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editMap, setEditMap] = useState<CompetencyMap | null>(null);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyMap, setCopyMap] = useState<CompetencyMap | null>(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  // Получение карт компетенций с бэкенда
  const { data: backendMaps = [], isLoading } = useQuery({
    queryKey: ['competency-maps'],
    queryFn: async () => api('/competency-maps'),
    staleTime: 5 * 60 * 1000,
  });

  // Мутация удаления
  const deleteMapMutation = useMutation({
    mutationFn: async (id: string) => api(`/competency-maps/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      showToast({
        title: t('competencyMaps.mapDeleted'),
        message: t('competencyMaps.mapDeletedMsg'),
        color: 'teal',
      });
      queryClient.invalidateQueries({ queryKey: ['competency-maps'] });
    },
    onError: (error) => {
      const message = getErrorMessage(error, t('competencyMaps.deleteError'));
      showToast({ title: t('common.error'), message, color: 'red' });
    },
  });

  const handleAdd = () => {
    setEditMap(null);
    setModalOpen(true);
  };

  const handleEdit = (map: CompetencyMap) => {
    setEditMap(map);
    setModalOpen(true);
  };

  const handleView = (map: CompetencyMap) => {
    navigate(`/competency-maps/${map._id}`);
  };

  const handleDeleteMap = (map: CompetencyMap) => {
    modals.openConfirmModal({
      title: t('competencyMaps.deleteConfirmTitle', { name: map.name }),
      centered: true,
      children: (
        <Text
          size="sm"
          dangerouslySetInnerHTML={{
            __html: t('competencyMaps.deleteConfirmText', { name: map.name }),
          }}
        />
      ),
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onCancel: () => {},
      onConfirm: () => {
        deleteMapMutation.mutate(map._id);
      },
    });
  };

  const handleCopyMap = (map: CompetencyMap) => {
    setCopyMap(map);
    setCopyModalOpen(true);
  };

  return (
    <PageContainer>
      <Group justify="space-between" mb={{ base: 'md', sm: 'xl' }}>
        <Title order={2}>{t('competencyMaps.title')}</Title>
        <Group gap="xs">
          {deviceType !== 'mobile' && backendMaps.length > 0 && (
            <Button
              variant="light"
              leftSection={<IconSparkles size={16} />}
              onClick={() => setIsWizardOpen(true)}
            >
              {t('competencyMaps.first.open')}
            </Button>
          )}
          {/* На пустом экране ручной путь уже есть рядом с мастером. */}
          {(isLoading || backendMaps.length > 0) && (
            <Button variant="light" leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              {deviceType === 'mobile' ? t('common.add') : t('competencyMaps.addMap')}
            </Button>
          )}
        </Group>
      </Group>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg" style={{ maxWidth: '100%' }}>
        {isLoading ? (
          <Group justify="center" py="xl" style={{ gridColumn: '1 / -1' }}>
            <Loader size="sm" />
          </Group>
        ) : backendMaps.length === 0 ? (
          // Пустой раздел — не тупик: сразу путь к первой карте, без знания «грейдов» и «свимлейнов».
          <Stack gap="xl" style={{ gridColumn: '1 / -1' }}>
            <Stack gap="sm" align="flex-start">
              <Title order={3}>{t('competencyMaps.first.emptyTitle')}</Title>
              <Text c="dimmed" maw={560}>
                {t('competencyMaps.first.emptyText')}
              </Text>
              <Group gap="sm" mt="xs">
                <Button
                  leftSection={<IconSparkles size={16} />}
                  onClick={() => setIsWizardOpen(true)}
                >
                  {t('competencyMaps.first.open')}
                </Button>
                <Button variant="subtle" onClick={handleAdd}>
                  {t('competencyMaps.first.manual')}
                </Button>
              </Group>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                {t('competencyMaps.first.exampleCaption')}
              </Text>
              {/* Настоящая карта на примере: нажимать нечего — таких компетенций нет в базе. */}
              <div inert>
                <CompetencyMapView
                  isDragDropEnabled={false}
                  cardSize="M"
                  hideGradeProgress
                  hideStatusAndActions
                  isExample
                  competencyMapData={exampleMap(t)}
                />
              </div>
            </Stack>
          </Stack>
        ) : (
          backendMaps.map((map: CompetencyMap) => (
            <Card
              key={map._id}
              shadow="sm"
              padding="md"
              radius="md"
              withBorder
              style={{
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
              onClick={() => handleView(map)}
            >
              <div style={{ flex: 1 }}>
                <Text size="md" fw={500}>
                  {map.name}
                </Text>
                <Text size="sm" c="dimmed">
                  {(map.professions || []).map((p) => p.name).join(', ')}
                </Text>
                {((map.grades && map.grades?.length > 0) ||
                  (map.swimlanes && map.swimlanes.length > 0)) && (
                  <Stack mt="xs" gap={0}>
                    {map.grades && map.grades.length > 0 && (
                      <Text size="xs" c="dimmed">
                        {t('competencyMaps.grades')}:{' '}
                        {map.grades.map((grade) => grade.title).join(', ')}
                      </Text>
                    )}
                    {map.swimlanes && map.swimlanes.length > 0 && (
                      <Text size="xs" c="dimmed">
                        {t('competencyMaps.swimlanes')}:{' '}
                        {map.swimlanes.map((swimlane) => swimlane.title).join(', ')}
                      </Text>
                    )}
                  </Stack>
                )}
              </div>
              <Group gap="xs" mt="md" onClick={(e) => e.stopPropagation()}>
                <ActionIcon variant="light" onClick={() => handleView(map)}>
                  <IconExternalLink size={16} />
                </ActionIcon>
                <ActionIcon variant="light" onClick={() => handleCopyMap(map)}>
                  <IconCopy size={16} />
                </ActionIcon>
                <ActionIcon variant="light" onClick={() => handleEdit(map)}>
                  <IconEdit size={16} />
                </ActionIcon>
                <ActionIcon variant="light" color="red" onClick={() => handleDeleteMap(map)}>
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </Card>
          ))
        )}
      </SimpleGrid>
      <CompetencyMapModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        editMap={editMap}
      />
      <FirstMapWizard opened={isWizardOpen} onClose={() => setIsWizardOpen(false)} />
      <CompetencyMapCopyModal
        opened={copyModalOpen}
        onClose={() => setCopyModalOpen(false)}
        competencyMap={copyMap}
      />
    </PageContainer>
  );
};
